import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { SnackbarOrigin, Dialog, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  VariantType,
  SnackbarProvider,
  enqueueSnackbar,
  closeSnackbar,
  SnackbarKey,
  SnackbarContent,
  CustomContentProps,
} from "notistack";
import usePersistedState from "@hooks/usePersistedState";
import { AlertElement } from "./Alert";
import { NotificationContextType, NotificationOptions } from "./notificationContextType";
import { NotificationType } from "./notificationType";

const defaultDuration = 2500;

const ANCHOR_CONFIG: SnackbarOrigin = {
  vertical: "top",
  horizontal: "right",
};

export const NotificationContext = createContext<NotificationContextType>(null!);

const DialogTransition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
    // eslint-disable-next-line react/jsx-props-no-spreading
  ) => <Slide direction="up" ref={ref} {...props} />,
);

const ErrorSnackbar = React.forwardRef<HTMLDivElement, CustomContentProps>((props, forwardedRef) => {
  const { message, id } = props;

  return (
    <SnackbarContent ref={forwardedRef}>
      <AlertElement onClick={() => closeSnackbar(id)} message={message as string} />
    </SnackbarContent>
  );
});

const NotificationProvider: React.FC<React.PropsWithChildren<{}>> = props => {
  const { state: notificationsState, setState: setNotificationsState } = usePersistedState(
    "notificationsList",
    {},
    "session",
  );

  const getSnackbarType = (type: NotificationType): VariantType => {
    switch (type) {
      case NotificationType.Success:
        return "success";
      case NotificationType.Warning:
        return "warning";
      case NotificationType.Error:
        return "error";
      case NotificationType.Default:
        return "default";
      case NotificationType.Info:
        return "info";
      default:
        return "default";
    }
  };

  const addNotification = useCallback(
    (
      type: NotificationType,
      text: string,
      options: {
        vertical?: SnackbarOrigin["vertical"];
        horizontal?: SnackbarOrigin["horizontal"];
        showDuration?: number;
        persist?: boolean;
        persistOnReload?: boolean;
      },
    ) => {
      const {
        showDuration,
        vertical = ANCHOR_CONFIG.vertical,
        horizontal = ANCHOR_CONFIG.horizontal,
        persist,
        persistOnReload,
      } = options;

      const snackbarType = getSnackbarType(type);
      const autoHideDuration = snackbarType === "success" ? defaultDuration : showDuration;

      if (persistOnReload) {
        setNotificationsState((prev: any) => ({ ...prev, [text]: { type, text, options } }));
      }

      enqueueSnackbar(text, {
        variant: snackbarType,
        autoHideDuration,
        preventDuplicate: true,
        anchorOrigin: {
          vertical,
          horizontal,
        },
        persist,
        onClose: () => {
          if (!persistOnReload) {
            return;
          }

          setNotificationsState((prev: any) => {
            const newState = { ...prev };
            delete newState[text];
            return newState;
          });
        },
      });
    },
    [],
  );

  useEffect(() => {
    Object.entries(notificationsState).forEach(([, value]: [string, any]) => {
      addNotification(value.type, value.text, value.options);
    });
  }, []);

  const [open, setOpen] = React.useState(false);
  const [messageDialog, setMessageDialog] = React.useState("");

  const openDialog = (text: string) => {
    setMessageDialog(text);
    setOpen(true);
  };

  const closeDialog = (_: React.SyntheticEvent, reason: string) => {
    if (reason !== "backdropClick") {
      setOpen(false);
      setMessageDialog("");
    }
  };

  const contextValue = useMemo(
    () => ({
      showSuccess: (text: string, showDuration?: number): void => {
        addNotification(NotificationType.Success, text, { showDuration });
      },
      // eslint-disable-next-line
      showError: (message: any, showDuration: number, options: NotificationOptions): void => {
        if (options?.isDialog) {
          openDialog(message);
        } else {
          addNotification(NotificationType.Error, message?.length ? message : "Error!", {
            showDuration,
            horizontal: "center",
            persist: true,
            persistOnReload: options?.persistOnReload,
          });
        }
      },
      showWarning: (text: string, showDuration?: number): void => {
        addNotification(NotificationType.Warning, text, { showDuration });
      },
      showDefault: (text: string, showDuration?: number): void => {
        addNotification(NotificationType.Default, text, { showDuration });
      },
      showInfo: (text: string, showDuration?: number): void => {
        addNotification(NotificationType.Info, text, { showDuration });
      },
      removeNotification: (key: SnackbarKey) => {
        closeSnackbar(key);
      },
    }),
    [addNotification],
  );

  const { children } = props;

  return (
    <NotificationContext.Provider value={contextValue}>
      <SnackbarProvider
        Components={{
          error: ErrorSnackbar,
        }}
        classes={{
          containerRoot: "notistack-root-style",
        }}
        anchorOrigin={ANCHOR_CONFIG}
        maxSnack={100}
      />
      <Dialog
        open={open}
        TransitionComponent={DialogTransition}
        keepMounted
        onClose={closeDialog}
        aria-describedby="alert-dialog-slide-description"
      >
        <AlertElement onClick={closeDialog} message={messageDialog} />
      </Dialog>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;

export function useNotification() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotification must be used within the NotificationContextProvider");
  }

  return context;
}
